import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Building2, Phone, Mail, MapPin, Upload, Download } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SupplierMasterPage() {
  const { user, getAuthHeader } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editSupplier, setEditSupplier] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    contact_person: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    gst_number: '',
    pan_number: '',
    payment_terms: ''
  });

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API_URL}/suppliers`, { headers: getAuthHeader() });
      setSuppliers(response.data);
    } catch (error) {
      toast.error('Failed to fetch suppliers');
    }
  };

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const resetForm = () => {
    setForm({
      name: '',
      contact_person: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      gst_number: '',
      pan_number: '',
      payment_terms: ''
    });
    setEditSupplier(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name) {
      toast.error('Supplier name is required');
      return;
    }

    try {
      if (editSupplier) {
        await axios.patch(`${API_URL}/suppliers/${editSupplier.id}`, form, { headers: getAuthHeader() });
        toast.success('Supplier updated');
      } else {
        await axios.post(`${API_URL}/suppliers`, form, { headers: getAuthHeader() });
        toast.success('Supplier created');
      }
      setOpen(false);
      resetForm();
      fetchSuppliers();
    } catch (error) {
      const errorDetail = error.response?.data?.detail; let errorMessage = 'Failed to save supplier'; if (typeof errorDetail === 'string') { errorMessage = errorDetail; } else if (Array.isArray(errorDetail)) { errorMessage = errorDetail.map(e => e.msg || JSON.stringify(e)).join(', '); } else if (errorDetail && typeof errorDetail === 'object') { errorMessage = errorDetail.msg || JSON.stringify(errorDetail); } toast.error(errorMessage);
    }
  };

  const handleEdit = (supplier) => {
    setEditSupplier(supplier);
    setForm({
      name: supplier.name || '',
      contact_person: supplier.contact_person || '',
      email: supplier.email || '',
      phone: supplier.phone || '',
      address: supplier.address || '',
      city: supplier.city || '',
      state: supplier.state || '',
      gst_number: supplier.gst_number || '',
      pan_number: supplier.pan_number || '',
      payment_terms: supplier.payment_terms || ''
    });
    setOpen(true);
  };

  const handleDelete = async (supplierId) => {
    if (!confirm('Are you sure you want to deactivate this supplier?')) return;
    
    try {
      await axios.delete(`${API_URL}/suppliers/${supplierId}`, { headers: getAuthHeader() });
      toast.success('Supplier deactivated');
      fetchSuppliers();
    } catch (error) {
      const errorDetail = error.response?.data?.detail; let errorMessage = 'Failed to delete supplier'; if (typeof errorDetail === 'string') { errorMessage = errorDetail; } else if (Array.isArray(errorDetail)) { errorMessage = errorDetail.map(e => e.msg || JSON.stringify(e)).join(', '); } else if (errorDetail && typeof errorDetail === 'object') { errorMessage = errorDetail.msg || JSON.stringify(errorDetail); } toast.error(errorMessage);
    }
  };

  const downloadTemplate = () => {
    const headers = ['NAME', 'CONTACT_PERSON', 'EMAIL', 'PHONE', 'ADDRESS', 'CITY', 'STATE', 'GST_NUMBER', 'PAN_NUMBER', 'PAYMENT_TERMS'];
    const csvContent = headers.join(',') + '\nABC SUPPLIERS,JOHN DOE,john@abc.com,9876543210,123 INDUSTRIAL AREA,MUMBAI,MAHARASHTRA,27AABCU9603R1ZM,AABCU9603R,NET 30';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'supplier_template.csv';
    link.click();
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/suppliers/bulk-upload`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Created ${response.data.created} suppliers`);
      if (response.data.errors?.length > 0) {
        toast.error(`${response.data.errors.length} errors occurred`);
        console.log('Upload errors:', response.data.errors);
      }
      fetchSuppliers();
    } catch (error) {
      const errorDetail = error.response?.data?.detail; let errorMessage = 'Failed to upload file'; if (typeof errorDetail === 'string') { errorMessage = errorDetail; } else if (Array.isArray(errorDetail)) { errorMessage = errorDetail.map(e => e.msg || JSON.stringify(e)).join(', '); } else if (errorDetail && typeof errorDetail === 'object') { errorMessage = errorDetail.msg || JSON.stringify(errorDetail); } toast.error(errorMessage);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  if (!user) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">You don&apos;t have permission to access this page</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Supplier Master</h1>
            <p className="text-muted-foreground">Manage your suppliers for purchase orders</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={downloadTemplate}>
              <Download className="w-4 h-4 mr-2" />
              Template
            </Button>
            <label>
              <Button variant="outline" disabled={uploading} asChild>
                <span>
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? 'Uploading...' : 'Bulk Upload'}
                </span>
              </Button>
              <input type="file" accept=".xlsx,.xls,.csv" onChange={handleBulkUpload} className="hidden" />
            </label>
            <Dialog open={open} onOpenChange={(val) => { setOpen(val); if (!val) resetForm(); }}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Supplier
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editSupplier ? 'Edit' : 'Add'} Supplier</DialogTitle>
                <DialogDescription>
                  {editSupplier ? 'Update supplier information' : 'Add a new supplier to the system'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-2 gap-4 py-4">
                  <div className="col-span-2 space-y-2">
                    <Label>Supplier Name *</Label>
                    <Input
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value.toUpperCase() })}
                      placeholder="ENTER SUPPLIER NAME"
                      required
                      style={{ textTransform: 'uppercase' }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Contact Person</Label>
                    <Input
                      value={form.contact_person}
                      onChange={(e) => setForm({ ...form, contact_person: e.target.value.toUpperCase() })}
                      placeholder="CONTACT PERSON NAME"
                      style={{ textTransform: 'uppercase' }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="Phone number"
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value.toLowerCase() })}
                      placeholder="email@example.com"
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Address</Label>
                    <Input
                      value={form.address}
                      onChange={(e) => setForm({ ...form, address: e.target.value.toUpperCase() })}
                      placeholder="STREET ADDRESS"
                      style={{ textTransform: 'uppercase' }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>City</Label>
                    <Input
                      value={form.city}
                      onChange={(e) => setForm({ ...form, city: e.target.value.toUpperCase() })}
                      placeholder="CITY"
                      style={{ textTransform: 'uppercase' }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>State</Label>
                    <Input
                      value={form.state}
                      onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })}
                      placeholder="STATE"
                      style={{ textTransform: 'uppercase' }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>GST Number</Label>
                    <Input
                      value={form.gst_number}
                      onChange={(e) => setForm({ ...form, gst_number: e.target.value.toUpperCase() })}
                      placeholder="GST NUMBER"
                      style={{ textTransform: 'uppercase' }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>PAN Number</Label>
                    <Input
                      value={form.pan_number}
                      onChange={(e) => setForm({ ...form, pan_number: e.target.value.toUpperCase() })}
                      placeholder="PAN NUMBER"
                      style={{ textTransform: 'uppercase' }}
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Payment Terms</Label>
                    <Input
                      value={form.payment_terms}
                      onChange={(e) => setForm({ ...form, payment_terms: e.target.value.toUpperCase() })}
                      placeholder="E.G., NET 30, ADVANCE"
                      style={{ textTransform: 'uppercase' }}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => { setOpen(false); resetForm(); }}>
                    Cancel
                  </Button>
                  <Button type="submit">{editSupplier ? 'Update' : 'Create'} Supplier</Button>
                </DialogFooter>
              </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Suppliers Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Suppliers ({suppliers.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>GST</TableHead>
                  <TableHead>Payment Terms</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {suppliers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12">
                      <Building2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                      <p className="text-muted-foreground">No suppliers yet</p>
                      <p className="text-sm text-muted-foreground mt-1">Add your first supplier to get started</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  suppliers.map((supplier) => (
                    <TableRow key={supplier.id}>
                      <TableCell>
                        <Badge variant="outline" className="font-mono">{supplier.supplier_code}</Badge>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{supplier.name}</p>
                          {supplier.contact_person && (
                            <p className="text-sm text-muted-foreground">{supplier.contact_person}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {supplier.phone && (
                            <div className="flex items-center gap-1 text-sm">
                              <Phone className="w-3 h-3 text-muted-foreground" />
                              {supplier.phone}
                            </div>
                          )}
                          {supplier.email && (
                            <div className="flex items-center gap-1 text-sm">
                              <Mail className="w-3 h-3 text-muted-foreground" />
                              {supplier.email}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {(supplier.city || supplier.state) && (
                          <div className="flex items-center gap-1 text-sm">
                            <MapPin className="w-3 h-3 text-muted-foreground" />
                            {[supplier.city, supplier.state].filter(Boolean).join(', ')}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {supplier.gst_number && (
                          <span className="text-sm font-mono">{supplier.gst_number}</span>
                        )}
                      </TableCell>
                      <TableCell>{supplier.payment_terms || '-'}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => handleEdit(supplier)}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(supplier.id)}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
